// Brain identity (docs/09 §6). `frontend/brain/VERSION` is the single semver
// source; both bundlers inject `__BRAIN_VERSION__` via esbuild/vite `define`
// (version + git short sha + build time + wire contract).
//
// Read order — `globalThis.__BRAIN_VERSION__` first so a host (or a test) can
// stamp a bundle it did not build, then the build-time define, then a safe
// `dev` fallback so vitest and the un-defined web build keep working. `typeof`
// guards the define: an undeclared identifier would be a ReferenceError.

export interface BrainVersion {
  version: string;
  commit: string;
  builtAt: string;
  contract: number;
}

declare const __BRAIN_VERSION__: Partial<BrainVersion> | undefined;

const FALLBACK: BrainVersion = { version: 'dev', commit: 'unknown', builtAt: '', contract: 2 };

/** The build stamp. Read lazily — a host may inject it after module load. */
export function brainVersion(): BrainVersion {
  const injected = (globalThis as { __BRAIN_VERSION__?: Partial<BrainVersion> })
    .__BRAIN_VERSION__;
  const defined = typeof __BRAIN_VERSION__ !== 'undefined' ? __BRAIN_VERSION__ : undefined;
  const stamp = injected ?? defined;
  if (!stamp) return FALLBACK;
  return {
    version: typeof stamp.version === 'string' ? stamp.version : FALLBACK.version,
    commit: typeof stamp.commit === 'string' ? stamp.commit : FALLBACK.commit,
    builtAt: typeof stamp.builtAt === 'string' ? stamp.builtAt : FALLBACK.builtAt,
    contract: typeof stamp.contract === 'number' ? stamp.contract : FALLBACK.contract,
  };
}

/** The `brain` block restated in every v2 snapshot (docs/09 §5) — no contract. */
export function brainStamp(): { version: string; commit: string; builtAt: string } {
  const v = brainVersion();
  return { version: v.version, commit: v.commit, builtAt: v.builtAt };
}

/** One line at start(), so a device log always says which brain is running. */
export function brainVersionLine(entry: string): string {
  const v = brainVersion();
  return `brain ${v.version} (${v.commit}) contract ${v.contract} entry ${entry} built ${v.builtAt || 'dev'}`;
}
